import { useVideo } from "../../hooks/useVideo";
import "react-html5video/dist/styles.css";
import ReactPlayer from "react-player";
import PropTypes from "prop-types";
import {
  FaCompress,
  FaVolumeUp,
  FaShareAlt,
  FaVolumeMute,
  FaPlay,
  FaPause,
  FaStepBackward,
  FaStepForward,
} from "react-icons/fa";
import "./player.scss";
import { useEffect, useRef, useState } from "react";
import { FaExpand } from "react-icons/fa";
import { BiCommentDetail } from "react-icons/bi";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { useDispatch } from "react-redux";
import { setHasInteracted } from "../slice/sliceSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import SocialShare from "./SocialShare";
import VideoDescription from "./VideoDescription";
import { useAddViewMutation } from "../../features/api/videoApiSlice";
import { getOrCreateSessionId } from "../../util/session";
import CommentsSection from "../comments/CommentsSection";
import { useGetCommentsQuery } from "../../features/api/commentsApiSlice";

const VideoPlayer = ({
  videos = [],
  isSubscribed,
  hasElevatedAccess,
  trialActive,
  video,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const playerRef = useRef(null);
  const [addView] = useAddViewMutation();
  // Player UI state
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [volume, setVolume] = useState(1);
  const [nextVideoPending, setNextVideoPending] = useState(null);

  const {
    selectedVideo,
    hasInteracted, // from Redux
    playerState,
    persistPlayerState,
    scrollToTop,
  } = useVideo();

  const [showComments, setShowComments] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const [isPosterVisible, setIsPosterVisible] = useState(true);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(() => {
    const stored = localStorage.getItem("autoPlayEnabled");
    return stored !== null ? JSON.parse(stored) : true;
  });

  const hasFullAccess =
    hasElevatedAccess ||
    isSubscribed ||
    trialActive ||
    selectedVideo?.videoType === "free";

  const { data: comments } = useGetCommentsQuery(selectedVideo?._id);
  const commentCount = comments?.length || 0;

  const [showControls, setShowControls] = useState(true);
  const hideTimeoutRef = useRef(null);
  const [showOverlay, setShowOverlay] = useState(true);

  // SINGLE source of truth for playing
  const [isPlaying, setIsPlaying] = useState(false);

  const currentIndex = videos.findIndex((v) => v?._id === selectedVideo?._id);
  const nextVideo =
    currentIndex >= 0 && currentIndex < videos?.length - 1
      ? videos[currentIndex + 1]
      : null;
  const prevVideo = currentIndex > 0 ? videos[currentIndex - 1] : null;

  // Load saved state from sessionStorage (unchanged)
  useEffect(() => {
    if (!video?._id || !playerRef.current) return;

    const saved = sessionStorage.getItem("videoPlayerState");
    if (saved) {
      const parsed = JSON.parse(saved);
      const state = parsed[video?._id];
      const internalPlayer = playerRef.current.getInternalPlayer?.();

      if (state && internalPlayer) {
        playerRef.current.seekTo(state?.progress || 0, "seconds");
        internalPlayer.volume = state?.volume ?? 1;
        setProgress(state?.progress || 0);
        setVolume(state?.volume ?? 1);
        setIsFullscreen(state?.isFullscreen || false);
      }
    }
  }, [video?._id]);

  // Persist state (unchanged)
  useEffect(() => {
    if (!video?._id) return;
    const toSave = {
      [video?._id]: { progress, volume, isFullscreen },
    };
    persistPlayerState(toSave);
    const existing = JSON.parse(
      sessionStorage.getItem("videoPlayerState") || "{}"
    );
    sessionStorage.setItem(
      "videoPlayerState",
      JSON.stringify({ ...existing, ...toSave })
    );
  }, [video?._id, progress, volume, isFullscreen]);

  // Restore persisted redux playerState (unchanged)
  useEffect(() => {
    if (!video?._id) return;
    const saved = playerState[video?._id];
    if (saved) {
      if (playerRef.current) {
        try {
          playerRef.current.seekTo(saved.progress || 0, "seconds");
          const internal = playerRef.current.getInternalPlayer?.();
          if (internal) internal.volume = saved.volume ?? 1;
        } catch (e) {}
      }
      setProgress(saved.progress || 0);
      setVolume(saved.volume ?? 1);
      setIsFullscreen(saved.isFullscreen || false);
    }
  }, [video?._id]);

  // reset UI state on video change
  useEffect(() => {
    setProgress(0);
    setDuration(0);
    setBuffered(0);
    setShowCountdown(false);
    setCountdown(5);
    setNextVideoPending(null);
    setIsPosterVisible(true);

    // If user has already interacted globally, allow autoplay; otherwise paused
    if (selectedVideo?.isFeatured) {
      setIsPlaying(false);
    } else {
      // Respect global hasInteracted: if user interacted previously, allow autoplay
      setIsPlaying(Boolean(hasInteracted));
      // If we want to trigger the internal player to play right away when hasInteracted true:
      if (hasInteracted) {
        // give ReactPlayer a tick to mount
        setTimeout(
          () => playerRef.current?.getInternalPlayer?.()?.play?.(),
          50
        );
      }
    }
    scrollToTop();
  }, [selectedVideo, hasInteracted]);

  // countdown effect for autoplay-next
  useEffect(() => {
    let timer;
    if (showCountdown && countdown > 0) {
      timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    } else if (showCountdown && countdown === 0 && nextVideoPending) {
      navigate(
        {
          pathname: `/watch/${nextVideoPending.slug || nextVideoPending._id}`,
          search: searchParams.toString(),
        },
        { state: { source: "videoList", videos } }
      );
      setNextVideoPending(null);
      setShowCountdown(false);
    }
    return () => clearTimeout(timer);
  }, [
    showCountdown,
    countdown,
    nextVideoPending,
    navigate,
    videos,
    searchParams,
  ]);


  const playVideo = () => {
    // mark interaction FIRST
    dispatch(setHasInteracted(true));

    setIsPlaying(true);
    setIsPosterVisible(false);

    if (!hasFullAccess) setShowOverlay(false);

    // ✅ DO NOT manually call internal.play()
    // ReactPlayer will play via `playing={true}`
  };

  const pauseVideo = () => {
    setIsPlaying(false);

    // restore poster for previews
    if (!hasFullAccess) {
      setIsPosterVisible(true);
      setShowOverlay(true);
    }
  };

  const handleNext = () => {
    if (nextVideo) {
      setShowCountdown(false);
      setCountdown(5);
      navigate(
        {
          pathname: `/watch/${nextVideo.slug || nextVideo._id}`,
          search: searchParams.toString(),
        },
        { state: { source: "videoList", videos } }
      );
    }
  };

  const handlePrev = () => {
    if (prevVideo) {
      setShowCountdown(false);
      setCountdown(5);
      navigate(
        {
          pathname: `/watch/${prevVideo.slug || prevVideo._id}`,
          search: searchParams.toString(),
        },
        { state: { source: "videoList", videos } }
      );
    }
  };

  const onEnded = () => {
    if (autoPlayEnabled && nextVideo) {
      setNextVideoPending(nextVideo);
      setShowCountdown(true);
      setCountdown(5);
    } else {
      setIsPlaying(false);
      // reset global interaction flag for previews so next loads behave predictably
      dispatch(setHasInteracted(false));
      setIsPosterVisible(true);
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
  };
  useEffect(() => () => clearTimeout(hideTimeoutRef.current), []);

  const toggleFullscreen = () => {
    const playerWrapper = document.querySelector(".video-player-wrapper");
    if (!document.fullscreenElement) {
      playerWrapper.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };
  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFsChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  // view tracking
  const hasSentViewRef = useRef(false);
  useEffect(() => {
    hasSentViewRef.current = false;
  }, [selectedVideo]);
  const handleView = async () => {
    if (!selectedVideo?._id || hasSentViewRef.current) return;
    hasSentViewRef.current = true;
    const sessionId = getOrCreateSessionId();
    try {
      await addView({ videoId: selectedVideo._id, sessionId }).unwrap();
    } catch (err) {
      console.error("Error adding view:", err);
    }
  };

  const isPaid = selectedVideo?.videoType === "paid";
  const videoUrl = hasFullAccess
    ? selectedVideo?.fullUrl
    : selectedVideo?.previewUrl;

  return (
    <div
      className="video-player-wrapper"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setShowControls(false)}
    >
      <div className="player-screen relative">
        <ReactPlayer
          key={selectedVideo?._id}
          ref={playerRef}
          url={videoUrl}
          playing={isPlaying}
          controls={false}
          className="react-player"
          width="100%"
          height="100%"
          volume={volume}
          // ✅ FIX: mute until user interacts, NEVER toggle with isPlaying
          muted={!hasInteracted}
          playsinline
          onEnded={onEnded}
          onPlay={() => {
            setIsPlaying(true);
            dispatch(setHasInteracted(true));
            setIsPosterVisible(false);
            handleView();
          }}
          onPause={() => {
            setIsPlaying(false);

            // bring poster back for previews
            if (!hasFullAccess) {
              setIsPosterVisible(true);
              setShowOverlay(true);
            }
          }}
          onProgress={({ playedSeconds, loadedSeconds }) => {
            setProgress(playedSeconds);
            setBuffered(loadedSeconds);
            if (selectedVideo?._id) {
              persistPlayerState({
                [selectedVideo._id]: {
                  progress: playedSeconds,
                  volume,
                  isFullscreen,
                },
              });
            }
          }}
          onDuration={(d) => setDuration(d)}
          config={{
            file: {
              attributes: {
                preload: "metadata", // ✅ safer than "auto" on mobile
                controlsList: "nodownload remoteplayback",
              },
            },
          }}
        />

        {/* Poster overlay */}
        {isPosterVisible && (
          <div
            className={`poster-container ${!isPosterVisible ? "fade-out" : ""}`}
            onClick={playVideo}
          >
            <img
              src={selectedVideo?.posterUrl || selectedVideo?.thumbUrl}
              alt={`Poster for ${selectedVideo?.title}`}
              className="poster-image"
            />
            <button className="poster-play-btn" onClick={playVideo}>
              <FaPlay />
            </button>
          </div>
        )}

        {showCountdown && nextVideo && (
          <div
            className="next-video-overlay"
            style={{
              backgroundImage: `url(${
                nextVideo?.posterUrl || nextVideo.thumbUrl
              })`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="overlay-content">
              <h3>Up next: {nextVideo.title}</h3>
              <p>Playing in {countdown} seconds...</p>
              <div className="overlay-buttons">
                <button
                  onClick={() => {
                    setShowCountdown(false);
                    setCountdown(5);
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    navigate(
                      {
                        pathname: `/watch/${nextVideo.slug || nextVideo._id}`,
                        search: searchParams.toString(),
                      },
                      { state: { source: "videoList", videos } }
                    );
                    setShowCountdown(false);
                  }}
                >
                  Play Now
                </button>
              </div>
            </div>
          </div>
        )}

        {showControls && hasInteracted && duration > 0 && (
          <div className="bottom-controls">
            <div className="center-controls">
              <span className="time">{formatTime(progress)}</span>
              <div className="progress-bar-container">
                <div className="progress-bar-wrapper">
                  <div
                    className="buffered-bar"
                    style={{ width: `${(buffered / duration) * 100}%` }}
                  ></div>
                  <div
                    className="played-bar"
                    style={{ width: `${(progress / duration) * 100}%` }}
                  ></div>
                  <input
                    type="range"
                    min={0}
                    max={duration}
                    step="0.1"
                    value={progress}
                    onChange={(e) => {
                      const newTime = parseFloat(e.target.value);
                      playerRef.current.seekTo(newTime, "seconds");
                      setProgress(newTime);
                    }}
                  />
                </div>
              </div>
              <span className="time">{formatTime(duration)}</span>
            </div>

            <div className="controls-row">
              <div className="left-controls">
                {prevVideo && (
                  <button onClick={handlePrev}>
                    <FaStepBackward />
                  </button>
                )}
                {isPlaying ? (
                  <button onClick={pauseVideo}>
                    <FaPause />
                  </button>
                ) : (
                  <button onClick={playVideo}>
                    <FaPlay />
                  </button>
                )}
                {nextVideo && (
                  <button onClick={handleNext}>
                    <FaStepForward />
                  </button>
                )}
              </div>

              <div className="right-controls">
                <div className="volume-control">
                  <button
                    onClick={() => {
                      const next = volume === 0 ? 1 : 0;
                      setVolume(next);

                      const internal = playerRef.current.getInternalPlayer?.();
                      if (internal) internal.volume = next;
                    }}
                  >
                    {volume === 0 ? <FaVolumeMute /> : <FaVolumeUp />}
                  </button>

                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={volume}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setVolume(val);

                      const internal = playerRef.current.getInternalPlayer?.();
                      if (internal) internal.volume = val;
                    }}
                  />
                </div>

                <button
                  onClick={() => {
                    setAutoPlayEnabled((prev) => {
                      const updated = !prev;
                      localStorage.setItem(
                        "autoPlayEnabled",
                        JSON.stringify(updated)
                      );
                      return updated;
                    });
                  }}
                  className={`autoplay-btn ${autoPlayEnabled ? "active" : ""}`}
                >
                  {autoPlayEnabled ? "Autoplay On" : "Autoplay Off"}
                </button>

                <button onClick={toggleFullscreen} className="fullscreen-btn">
                  {isFullscreen ? <FaCompress /> : <FaExpand />}
                </button>
              </div>
            </div>
          </div>
        )}

        {isPaid && !hasFullAccess && showOverlay && !showCountdown && (
          <div className="video-membership-text show">
            <div>
              <p>
                You’re watching a <strong>preview</strong>. Subscribe to unlock
                the full video.
              </p>
              {!hasElevatedAccess && (
                <button onClick={() => navigate("/member")}>
                  Subscribe To Become a Member
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="player-video-details">
        <div className="video-player-actions">
          <span>
            <MdOutlineRemoveRedEye className="player-icons" /> Views:{" "}
            {selectedVideo?.totalViews ?? selectedVideo?.numViews?.length ?? 0}
          </span>
          <button onClick={() => setShowComments((s) => !s)}>
            <BiCommentDetail className="player-icons" /> Comments:{" "}
            {commentCount}
          </button>
          <button onClick={() => setShowShare((s) => !s)}>
            <FaShareAlt className="player-icons" /> Share
          </button>
        </div>

        <h2 className="player-title-mobile">
          {selectedVideo?.title || selectedVideo?.videoTitle}
        </h2>
        <div className="player-actions">
          <h2 className="player-title">
            {selectedVideo?.title || selectedVideo?.videoTitle}
          </h2>
        </div>

        <VideoDescription
          description={
            selectedVideo?.description || selectedVideo?.videoDescription
          }
        />
        {showShare && (
          <SocialShare
            videoTitle={selectedVideo?.title}
            videoId={selectedVideo?._id}
          />
        )}
        {showComments && <CommentsSection videoId={selectedVideo?._id} />}
      </div>
    </div>
  );
};

// small helper left in component scope:
function formatTime(seconds) {
  if (isNaN(seconds)) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
}

export default VideoPlayer;
